/**
 * Zod schemas for every HTTP request body, plus the header helpers that the
 * conditional-write and idempotency middleware rely on.
 *
 * These schemas are the only place request shape is described. Route handlers
 * do not re-check types: if a handler receives a value, it came out of `parse`
 * here. That rule is what makes `noUncheckedIndexedAccess` and `strict` useful
 * rather than noisy, because a handler never has to reason about a field that
 * might be the wrong type.
 *
 * Validation policy, applied consistently:
 *
 * - Strings are trimmed and length-bounded. An unbounded string field is a
 *   storage-exhaustion vector, and a title of 4 MB is not a title.
 * - Unknown keys are *stripped*, not rejected. Stripping lets an older Worker
 *   serve a newer client that added an optional field, which is the direction
 *   you want during a rolling deploy. Rejecting would make every client bump a
 *   breaking change.
 * - Enums come from the `as const` arrays in `domain.ts`, so adding a priority
 *   or a role is a one-line change that the compiler propagates everywhere.
 */

import { z } from 'zod';
import { PRIORITIES, ROLES } from './domain.js';

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/**
 * Email is validated by shape only, lowercased and trimmed.
 *
 * Deliberately not RFC 5322: the full grammar accepts addresses with comments
 * and quoted local parts that no mail provider issues, and a regex claiming to
 * implement it is a well-known source of false rejections. The practical
 * validation is the uniqueness constraint on `users.email` plus the fact that
 * nothing in Slate sends mail - an invite to a typo'd address simply never
 * gets accepted.
 */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(254)
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'must be an email address');

/**
 * Password policy is a length floor and nothing else.
 *
 * Composition rules (an uppercase, a digit, a symbol) are excluded on purpose:
 * they push users toward `Password1!` and measurably reduce entropy while
 * making the password harder to remember. 12 characters with no composition
 * requirement is stronger than 8 with four, and it composes better with the
 * password managers that users should be using. The real defence is the 210k
 * PBKDF2 work factor - see docs/SECURITY.md.
 */
export const passwordSchema = z.string().min(12, 'must be at least 12 characters').max(200);

export const idSchema = z.string().uuid();

export const dateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD')
  .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), 'must be a real calendar date');

/** Colour values are stored, not parsed into components, but they are
 * constrained to hex so that a stored value can be interpolated into CSS
 * without a sanitiser in between. */
export const colourSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'must be a six-digit hex colour');

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(48)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be lowercase letters, digits and single hyphens');

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const registerBodySchema = z.object({
  email: emailSchema,
  name: z.string().trim().min(1).max(80),
  password: passwordSchema,
});

export const loginBodySchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});

/** Refresh takes the token from an HttpOnly cookie. The body form exists for
 * non-browser clients (a CLI, an integration test) that cannot hold a cookie
 * jar; the cookie takes precedence when both are present so a stale body value
 * cannot override a freshly rotated cookie. */
export const refreshBodySchema = z
  .object({
    refreshToken: z.string().min(20).max(512).optional(),
  })
  .default({});

// ---------------------------------------------------------------------------
// Workspaces
// ---------------------------------------------------------------------------

export const createWorkspaceBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  slug: slugSchema.optional(),
});

export const inviteMemberBodySchema = z.object({
  email: emailSchema,
  role: z.enum(ROLES).default('member'),
});

export const changeRoleBodySchema = z.object({
  role: z.enum(ROLES),
});

// ---------------------------------------------------------------------------
// Boards
// ---------------------------------------------------------------------------

export const createBoardBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).nullish(),
  /** Seeded columns let the client create a board that is immediately usable
   * instead of a bare surface the user has to populate by hand. */
  columns: z.array(z.string().trim().min(1).max(60)).max(12).optional(),
});

export const updateBoardBodySchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(2000).nullish(),
  })
  .refine((value) => value.name !== undefined || value.description !== undefined, {
    message: 'at least one of name or description is required',
  });

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

export const createColumnBodySchema = z.object({
  name: z.string().trim().min(1).max(60),
});

export const updateColumnBodySchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    /** Ordinal target index, matching the move semantics used for cards. */
    position: z.number().int().min(0).max(10_000).optional(),
    expectedVersion: z.number().int().min(1).optional(),
  })
  .refine((value) => value.name !== undefined || value.position !== undefined, {
    message: 'at least one of name or position is required',
  });

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export const createCardBodySchema = z.object({
  /** Optional client-minted id. Accepting it lets an optimistic insert render
   * with its final identity immediately, so the server's echo is a no-op
   * reconciliation instead of an id swap that would remount the DOM node and
   * cancel the user's in-flight drag. The server treats it as a hint: it is
   * rejected with 409 if the id already exists. */
  id: idSchema.optional(),
  columnId: idSchema,
  title: z.string().trim().min(1).max(280),
  description: z.string().trim().max(20_000).nullish(),
  assigneeId: idSchema.nullish(),
  dueDate: dateOnlySchema.nullish(),
  priority: z.enum(PRIORITIES).default('none'),
  labelIds: z.array(idSchema).max(20).optional(),
});

export const updateCardBodySchema = z
  .object({
    title: z.string().trim().min(1).max(280).optional(),
    description: z.string().trim().max(20_000).nullish(),
    assigneeId: idSchema.nullish(),
    dueDate: dateOnlySchema.nullish(),
    priority: z.enum(PRIORITIES).optional(),
    labelIds: z.array(idSchema).max(20).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'at least one field is required',
  });

/**
 * Move payload.
 *
 * `position` here is an **ordinal**: the zero-based index the card should
 * occupy in the destination column's list, after the card itself has been
 * removed from it. It is not the stored fractional `cards.position` value.
 *
 * That distinction is the whole reason the field is safe. A client that sent a
 * raw fractional value would be sending a guess computed against a column
 * snapshot that may already be stale; two peers dragging simultaneously would
 * both compute the same midpoint and one card would land on top of the other.
 * An ordinal is a statement of *intent* ("I want to be third") that the
 * Durable Object resolves against the column's actual current contents at the
 * moment it serialises the write. The server, not the client, owns the
 * fractional arithmetic.
 */
export const moveCardBodySchema = z.object({
  columnId: idSchema,
  position: z.number().int().min(0).max(100_000),
  /** Optimistic concurrency token. Required, not optional: a move without a
   * version check is a lost-update bug waiting for two people to drag the same
   * card at once. */
  expectedVersion: z.number().int().min(1),
  /** Client-generated correlation id, echoed on the resulting event so the
   * originating tab can retire its optimistic entry. */
  opId: z.string().min(8).max(64).optional(),
});

export const archiveCardBodySchema = z.object({
  archived: z.boolean(),
  expectedVersion: z.number().int().min(1),
});

/** Delete is unconditional in the sense that it does not need a version - a
 * delete cannot be lost, because deleting an already-deleted row is a no-op
 * rather than a data loss. `expectedVersion` is still accepted so the client
 * can surface "someone else changed this card while your confirm dialog was
 * open" before destroying their work. */
export const deleteCardQuerySchema = z.object({
  expectedVersion: z.coerce.number().int().min(1).optional(),
});

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

export const createCommentBodySchema = z.object({
  body: z.string().trim().min(1).max(10_000),
});

export const updateCommentBodySchema = z.object({
  body: z.string().trim().min(1).max(10_000),
});

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

/**
 * Cursor pagination rather than offset pagination.
 *
 * `board_events` is append-only and the activity panel reads it newest-first
 * while new events stream in at the top. With `?offset=20`, a page fetched
 * after three new events arrive repeats three rows the client already has -
 * a visible duplicate. An opaque cursor that encodes "everything strictly
 * before sequence 41" is stable under insertion, which is the only property
 * that matters for a feed that grows while you read it.
 */
export const activityQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(30),
  cursor: z.string().max(512).optional(),
});

// ---------------------------------------------------------------------------
// Header helpers
// ---------------------------------------------------------------------------

/**
 * Parse an `If-Match` header into an expected version.
 *
 * `If-Match` is defined by RFC 9110 as a list of entity tags, and Slate uses a
 * bare integer as the tag. Accepting the quoted form (`"3"`) as well as the
 * bare form costs three characters of code and avoids a class of bug where a
 * client library helpfully adds quotes and the server silently ignores the
 * precondition - which would turn every conditional write into an
 * unconditional one without any error to notice.
 *
 * Returns `null` when the header is absent (the caller decides whether that is
 * acceptable) and `undefined` when it is present but unparseable (the caller
 * must reject with 400 rather than fall back to unconditional).
 */
export function parseExpectedVersion(header: string | null | undefined): number | null | undefined {
  if (header === null || header === undefined || header.trim() === '') return null;

  const trimmed = header.trim();
  const unquoted = trimmed.startsWith('"') && trimmed.endsWith('"') ? trimmed.slice(1, -1) : trimmed;
  // `*` is a legal If-Match value meaning "any current representation". Slate
  // treats it as "no precondition", which is the closest honest mapping.
  if (unquoted === '*') return null;

  const parsed = Number(unquoted);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
}

/** The version precondition may also travel in the body (`expectedVersion`)
 * because the WebSocket protocol has no headers. Both paths funnel here so the
 * rule is stated once. */
export function resolveExpectedVersion(
  headerValue: string | null | undefined,
  bodyValue: number | undefined,
): { ok: true; version: number | null } | { ok: false } {
  const fromHeader = parseExpectedVersion(headerValue);
  if (fromHeader === undefined) return { ok: false };
  if (fromHeader !== null) return { ok: true, version: fromHeader };
  if (bodyValue !== undefined) return { ok: true, version: bodyValue };
  return { ok: true, version: null };
}
